import { HyperlinkProps } from '@workday/canvas-kit-react/button';
import { StyledType, createComponent } from '@workday/canvas-kit-react/common';
import invariant from 'invariant';
import { Canvas } from '../Canvas';
import { RoutedAnchor } from './RoutedAnchor';

export interface DevsiteLinkModel {
  url?: string;
  newTab?: boolean;
  display?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Grandfathered in
  onClick?: (e?: any) => void;
  download?: boolean | string;
  /** render a plain anchor even for an in-app path (e.g. a path served by the backend rather than the SPA, or one that may be a download) */
  forceNoRouter?: boolean;
  skipIndexTab?: boolean;
}

export interface DevsiteLinkProps extends DevsiteLinkModel, StyledType, HyperlinkProps {
  className?: string;
}

/**
 * The site's standard text link: Canvas Hyperlink styling plus in-app routing (via `RoutedAnchor`). Use for any
 * visible link in page content. Takes `url` rather than `href`, and renders its content unlinked when `url` is empty.
 */
const DevsiteLink = createComponent('a')({
  displayName: 'DevsiteLink',
  Component: (
    {
      children,
      className,
      display,
      download,
      forceNoRouter,
      newTab,
      onClick,
      skipIndexTab,
      url,
      ...props
    }: DevsiteLinkProps,
    ref
  ) => {
    invariant(!display || !children, 'Only one of properties "display" or "children" may be specified.');
    const content = display || children;

    if (!url) return <>{content}</>;

    const standardProps = {
      onClick,
      className,
      ...(newTab
        ? {
            target: '_blank',
            rel: 'noopener noreferrer',
          }
        : {}),
      ...props,
    };
    return (
      <Canvas.Hyperlink
        ref={ref}
        as={RoutedAnchor}
        href={url}
        download={download}
        forceNoRouter={forceNoRouter}
        {...standardProps}
        tabIndex={skipIndexTab && -1}
      >
        {content}
      </Canvas.Hyperlink>
    );
  },
});

export default DevsiteLink;
